import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { autoinject } from "aurelia-framework";
import { RulesRoutes } from "../../constants/rules/rules.constants";
import { RuleConfigModel, SaveRuleConfigResponse, ReorderBusinessUnitRuleConfigRequest, RiskCategoryLevelEnum, BaseRuleRiskSettingModel, RuleCollectionModel, SaveRuleCollectionResponse, AssignBuRuleCollectionsRequest, AssignRuleCollectionsRequest, BusinessUnitRuleCollectionModel, RuleCollectionClientModel, AssignRuleCollectionsToRuleRequest, SaveRuleCollectionRightStoreSetupRequest, SaveRuleCollectionRightStoreSetupResponse, GetRuleCollectionRightStoreSetupResponse, GetRuleCollectionRightStoreSetupsResponse, GetBuRuleCollectionModel, SaveRuleCollectionAiPrompt, RuleCollectionAiPromptRequest, RuleCollectionAiPromptResponse } from "../../models/rules/rules.models";
import { EntityMetaDataModel } from "../../models/queries/queries.models";
import { RequestConstants } from "@rightship-tech/common-ui/constants/request.constants";
import { FileHelper } from "../../helpers/file-helper";
import { ConfigurationService } from "@rightship-tech/common-ui/services/basic/configuration.service";

@autoinject
export class RulesClientService {
    _entityMetadataPromise: Promise<EntityMetaDataModel[]>;

    constructor(private requestClient: RequestClientService, private configurationService: ConfigurationService) { }

    public testAiPrompt(request:  {title:string, clauses:string, prompt:string, parameters:any}): Promise<{result:string, prompt:string}> {
        return this.requestClient.post(RulesRoutes.TestAiPrompt, {  },request);
    }

    public getRuleConfig(ruleConfigId: number): Promise<RuleConfigModel> {
        return this.requestClient.get(RulesRoutes.GetRuleConfig, { ruleConfigId });
    }

    public getRuleCollectionAiPrompt(ruleCollectionId: number, businessUnitId?:number): Promise<RuleCollectionAiPromptResponse> {
        return this.requestClient.get(RulesRoutes.GetRuleCollectionAiPrompt, { ruleCollectionId, businessUnitId });
    }

    public getRuleCollectionAssignments(ruleCollectionId: number): Promise<BusinessUnitRuleCollectionModel[]> {
        return this.requestClient.get(RulesRoutes.GetRuleCollectionAssignments, { ruleCollectionId });
    }

    public reorderBusinessUnitRules(request: ReorderBusinessUnitRuleConfigRequest): Promise<void> {
        return this.requestClient.post(RulesRoutes.ReorderBusinessUnitRules, {}, request);
    }

    public getAdvancedParameterDisplayText(vetId: number, ruleConfigParameterId: number, ruleConfigAdvancedParameterIds: number[]): Promise<string> {
        return this.requestClient.get(RulesRoutes.GetAdvancedParameterDisplayText, { vetId: vetId, ruleConfigParameterId: ruleConfigParameterId, ruleConfigAdvancedParameterIds: ruleConfigAdvancedParameterIds });
    }

    public getAdvancedParameterValue(vetId: number, ruleConfigParameterId: number, ruleConfigAdvancedParameterIds: number[]): Promise<string> {
        return this.requestClient.get(RulesRoutes.GetAdvancedParameterValue, { vetId: vetId, ruleConfigParameterId: ruleConfigParameterId, ruleConfigAdvancedParameterIds: ruleConfigAdvancedParameterIds });
    }

    public saveRuleConfig(ruleConfig: RuleConfigModel): Promise<SaveRuleConfigResponse> {
        return this.requestClient.post(RulesRoutes.SaveRuleConfig, {}, { ruleConfig });
    }

    public getRiskCategorySettings(level: RiskCategoryLevelEnum, id: number): Promise<BaseRuleRiskSettingModel[]> {
        return this.requestClient.get(`${RulesRoutes.GetRiskCategorySettings}/${level}/${id}`, {});
    }

    public saveRiskCategorySettings<T>(level: RiskCategoryLevelEnum, settings: T[]): Promise<void> {
        return this.requestClient.post(`${RulesRoutes.SaveRiskCategorySettings}/${level}`, {}, settings);
    }
    
    public assignRuleCollections(request: AssignRuleCollectionsRequest): Promise<void> {
        return this.requestClient.post(RulesRoutes.AssignRuleCollections, {}, request);
    }

    public deleteRules(ruleIds: number[]): Promise<void> {
        return this.requestClient.post(RulesRoutes.DeleteRules, {}, ruleIds);
    }

    public saveRuleCollection(ruleCollection: RuleCollectionModel): Promise<SaveRuleCollectionResponse> {
        return this.requestClient.post(RulesRoutes.SaveRuleCollection, {}, { ruleCollection });
    }

    public saveRuleCollectionAiPrompt(ruleCollectionAiPromptRequest: RuleCollectionAiPromptRequest): Promise<SaveRuleCollectionAiPrompt> {
        return this.requestClient.post(RulesRoutes.SaveRuleCollectionAiPrompt, {}, ruleCollectionAiPromptRequest);
    }

    public deleteRuleCollections(ruleCollectionIds: number[]): Promise<void> {
        return this.requestClient.post(RulesRoutes.DeleteRuleCollections, {}, ruleCollectionIds);
    }

    public getRuleCollection(ruleCollectionId: number): Promise<RuleCollectionModel> {
        return this.requestClient.get(RulesRoutes.GetRuleCollection, { ruleCollectionId: ruleCollectionId });
    }

    public getRuleCollectionDisplayName(ruleCollectionId: number): Promise<string> {
        return this.requestClient.get(RulesRoutes.GetRuleCollectionDisplayName, { ruleCollectionId: ruleCollectionId });
    }

    public getRuleCollections(excludeBuCollections: boolean = true): Promise<RuleCollectionClientModel[]> {
        return this.requestClient.get(RulesRoutes.GetRuleCollections, { excludeBuCollections: excludeBuCollections});
    }

    public getAvailableRuleCollections(businessUnitId): Promise<RuleCollectionClientModel[]> {
        return this.requestClient.get(`${RulesRoutes.GetAvailableRuleCollections}/${businessUnitId}`, {});
    }

    public getRuleCollectionsForRule(ruleId: number): Promise<RuleCollectionClientModel[]> {
        return this.requestClient.get(`${RulesRoutes.GetRuleCollectionsForRule}/${ruleId}`, { });
    }

    public getBuRuleCollections(businessUnitId: number, vettingTypeId:number, includeInternal:boolean): Promise<GetBuRuleCollectionModel[]> {
        return this.requestClient.get(`${RulesRoutes.GetBuRuleCollections}/${businessUnitId}`, {vettingTypeId:vettingTypeId, includeInternal:includeInternal});
    }

    public assignBuRuleCollections(request: AssignBuRuleCollectionsRequest): Promise<void> {
        return this.requestClient.post(RulesRoutes.AssignBuRuleCollections, {}, request);
    }

    public assignRuleCollectionsToRule(request: AssignRuleCollectionsToRuleRequest): Promise<void> {
        return this.requestClient.post(RulesRoutes.AssignRuleCollectionsToRule, {}, request);
    }

    public getRuleConfigs(): Promise<{ruleName:string, ruleCode:string, ruleConfigId:number, ruleCategoryId:number}[]> {
        return this.requestClient.get(RulesRoutes.GetRuleConfigs, {});
    }

    public getEntityModels(): Promise<EntityMetaDataModel[]> {
        if (!this._entityMetadataPromise) {
            this._entityMetadataPromise = this.requestClient.get<EntityMetaDataModel[]>(RulesRoutes.EntityMetaData, null);
        }
        return this._entityMetadataPromise;
    }

    public setVettingCommentAction(action: number, selectedItemsRequest: {ruleConfigs: {businessUnitId:number, ruleCollectionId:number, ruleCollectionRuleConfigId:number}[]}): Promise<void> {
        return this.requestClient.post(`${RulesRoutes.SetVettingCommentAction}/${action}`, {}, selectedItemsRequest);
    }

    public overrideVettingAction(ruleConfigs: {businessUnitId:number, ruleCollectionId:number, ruleCollectionRuleConfigId:number}[], vettingActionOverride: string): Promise<void> {
        const request = {
            vettingActionOverride: vettingActionOverride,
            ruleConfigs: ruleConfigs
        };
        return this.requestClient.post(RulesRoutes.OverrideVettingAction, {}, request);
    }

    public overrideVettingRisk(ruleConfigs: {businessUnitId:number, ruleCollectionId:number, ruleCollectionRuleConfigId:number}[], riskLevelId: number, riskCategoryId: number): Promise<void> {
        const request = {
            riskLevelId: riskLevelId,
            riskCategoryId: riskCategoryId,
            ruleConfigs: ruleConfigs
        };
        return this.requestClient.post(RulesRoutes.OverrideVettingRisk, {}, request);
    }

    public downloadRuleBusinessUnitsExcel(ruleConfigId: number, ruleConfigName?: string) {
        return new Promise<void>((resolve, reject) => {
            try {
                const url = "api/v1/Rule/DownloadBusinessUnitsAsExcel?ruleConfigId=" + ruleConfigId;

                $.ajax({
                    type: "GET",
                    url: url,
                    beforeSend: (request) => {
                        const headers = this.configurationService.getList(RequestConstants.ConfigurationTypes.RequestHeaders);
                        const keys = Object.keys(headers);
                        keys.forEach(key => {
                            request.setRequestHeader(key, headers[key].toString());
                        });
                    },
                    success: x => {
                        const url = window.URL.createObjectURL(x);
                        FileHelper.downloadFile(url, (ruleConfigName ? ruleConfigName + '- ' : '') + "Business Units.xlsx");

                        setTimeout(() => {
                            window.URL.revokeObjectURL(url);
                            resolve();
                        })
                    },
                    error: () => {
                        reject();
                    },
                    xhr: () => {
                        const xhr = new XMLHttpRequest();
                        xhr.responseType = 'blob';
                        return xhr;
                    }
                });
            }
            catch (exception) {
                resolve();
            }
        });
    }

    public getRuleCollectionRightStoreSetup(rightStoreSetupId: number): Promise<GetRuleCollectionRightStoreSetupResponse>
    {
        return this.requestClient.get(RulesRoutes.GetRuleCollectionRightStoreSetup, { rightStoreSetupId });
    }

    public saveRuleCollectionRightStoreSetup(request: SaveRuleCollectionRightStoreSetupRequest): Promise<SaveRuleCollectionRightStoreSetupResponse>
    {
        return this.requestClient.post(RulesRoutes.SaveRuleCollectionRightStoreSetup, {}, request);
    }

    public getRuleCollectionRightStoreSetups(): Promise<GetRuleCollectionRightStoreSetupsResponse>
    {
        return this.requestClient.get(RulesRoutes.GetRuleCollectionRightStoreSetups, { });
    }
}